import { z } from 'zod'
import { uuidSchema } from '../validators/uuid.validator.js'
import { proposalChapterSchema } from '../enums/proposal-chapter.enum.js'
import { proposalPlanSchema } from '../enums/proposal-plan.enum.js'
import {
  activityMoneyDetailSchema,
  activityTextDetailSchema,
} from '../input/proposal-activity.input.js'

const recentAtSchema = z.string().datetime()

/**
 * One line of the timeline.
 *
 * The same kinds as a recorded event, without the clock the page sent: `at`
 * is when it happened. A kind that carries no detail does not grow an empty
 * one on the way out, and a detail the kind does not allow is not a line.
 */
export const recentActivityEventSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('OPENED'), at: recentAtSchema }).strict(),
  z
    .object({
      kind: z.literal('READ'),
      detail: proposalChapterSchema,
      at: recentAtSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('TREE'),
      detail: activityTextDetailSchema,
      at: recentAtSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('PLAN'),
      detail: proposalPlanSchema,
      at: recentAtSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('BUDGET'),
      detail: activityMoneyDetailSchema,
      at: recentAtSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('ADJUSTED'),
      detail: activityTextDetailSchema,
      at: recentAtSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('VIDEO'),
      detail: activityTextDetailSchema,
      at: recentAtSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('PRINTED'),
      detail: z.literal('pdf').nullable(),
      at: recentAtSchema,
    })
    .strict(),
  z.object({ kind: z.literal('SHARED'), at: recentAtSchema }).strict(),
  z.object({ kind: z.literal('PRESENTED'), at: recentAtSchema }).strict(),
])

/**
 * What the client did with one version, summed up for the salesperson.
 *
 * Aggregated on the server rather than shipped as raw events: the page that
 * shows it wants "read the plan for four minutes, tried Hazards first twice",
 * not two hundred rows.
 */
export const proposalActivitySummaryDtoSchema = z.object({
  versionId: uuidSchema,
  /** Separate openings of the page. */
  opens: z.number().int().nonnegative(),
  firstOpenedAt: z.string().datetime().nullable(),
  lastSeenAt: z.string().datetime().nullable(),
  /** Seconds with a chapter in view, summed. */
  totalSeconds: z.number().int().nonnegative(),
  /**
   * Chapters by time spent, most first.
   *
   * A chapter id the page does not have is dropped while the summary is
   * built, so this list only contains ids the chapter list can name.
   */
  chapters: z.array(
    z.object({
      chapter: proposalChapterSchema,
      seconds: z.number().int().nonnegative(),
    }),
  ),
  plansTried: z.array(
    z.object({
      plan: proposalPlanSchema,
      count: z.number().int().positive(),
      lastAt: z.string().datetime(),
    }),
  ),
  /** Budgets typed into "Fit to a budget", in dollars, newest first. */
  budgets: z.array(
    z.object({ amount: activityMoneyDetailSchema, at: z.string().datetime() }),
  ),
  /** Changes made under "Adjust what's included", in words, newest first. */
  adjustments: z.array(
    z.object({ text: activityTextDetailSchema, at: z.string().datetime() }),
  ),
  /** Trees opened on the plan, most opened first. */
  treesOpened: z.array(
    z.object({
      label: activityTextDetailSchema,
      count: z.number().int().positive(),
    }),
  ),
  videosWatched: z.array(activityTextDetailSchema),
  printed: z.boolean(),
  shared: z.boolean(),
  presented: z.boolean(),
  /** The latest events, newest first, for the timeline. */
  recent: z.array(recentActivityEventSchema),
})

export type ProposalActivitySummaryDto = z.infer<
  typeof proposalActivitySummaryDtoSchema
>
